import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSettings } from "./SettingsContext";
import { activeInstrumentFromSettings } from "../lib/instruments";
import {
  downgradeNonFinancialBreaking,
  ensureScoring,
  type RiskFlowAlert,
} from "../lib/riskflow-feed";
import { useRiskFlowArrivals } from "./riskflow/use-riskflow-arrivals";
import { useRiskFlowFeed } from "./riskflow/use-riskflow-feed";

interface RiskFlowContextValue {
  alerts: RiskFlowAlert[];
  highCount: number;
  mediumCount: number;
  lowCount: number;
  clearAll: () => void;
  removeAlert: (id: string) => void;
  markSeen: (id: string) => void;
  markAllSeen: (ids: string[]) => void;
  isSeen: (id: string) => boolean;
  refresh: () => Promise<void>;
  refreshing: boolean;
  fetchStatus: string;
  loadMore: () => Promise<void>;
  loadingMore: boolean;
  hasMore: boolean;
  initialLoaded: boolean;
  freshAlertId: string | null;
}

const EMPTY_CONTEXT: RiskFlowContextValue = {
  alerts: [],
  highCount: 0,
  mediumCount: 0,
  lowCount: 0,
  clearAll: () => {},
  removeAlert: () => {},
  markSeen: () => {},
  markAllSeen: () => {},
  isSeen: () => false,
  refresh: async () => {},
  refreshing: false,
  fetchStatus: "",
  loadMore: async () => {},
  loadingMore: false,
  hasMore: false,
  initialLoaded: false,
  freshAlertId: null,
};

const RiskFlowContext = createContext<RiskFlowContextValue>(EMPTY_CONTEXT);
const SEEN_STORAGE_KEY = "fintheon:riskflow-seen:v1";

export function RiskFlowProvider({ children }: { children: React.ReactNode }) {
  const { selectedInstrument, selectedSymbol } = useSettings();
  const activeInstrument = activeInstrumentFromSettings({
    selectedInstrument,
    selectedSymbol,
  });
  const feed = useRiskFlowFeed(activeInstrument);
  const freshAlertId = useRiskFlowArrivals(feed.alerts, feed.alertScope);
  const [seenIds, setSeenIds] = useState<Set<string>>(() =>
    loadStoredIds(SEEN_STORAGE_KEY),
  );
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const scoredAlerts = useMemo(() => {
    const copies = feed.alerts.map((alert) => ({ ...alert }));
    ensureScoring(copies, activeInstrument);
    downgradeNonFinancialBreaking(copies);
    return copies;
  }, [activeInstrument, feed.alerts]);
  const visibleAlerts = scoredAlerts.filter(
    (alert) => !dismissedIds.has(alert.id),
  );
  const counts = countBySeverity(visibleAlerts);

  const mergedIdsRef = useRef<string[]>([]);
  const mergedIds = scoredAlerts.map((alert) => alert.id);
  const mergedIdsKey = mergedIds.join(",");
  if (mergedIdsRef.current.join(",") !== mergedIdsKey) {
    mergedIdsRef.current = mergedIds;
  }

  const clearAll = useCallback(() => {
    setSeenIds((current) => {
      const next = new Set(current);
      mergedIdsRef.current.forEach((id) => next.add(id));
      return next;
    });
  }, [mergedIdsKey]);

  const removeAlert = useCallback((id: string) => {
    setDismissedIds((current) => new Set(current).add(id));
  }, []);

  const markSeen = useCallback((id: string) => {
    if (!id) return;
    setSeenIds((current) =>
      current.has(id) ? current : new Set(current).add(id),
    );
  }, []);

  const markAllSeen = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setSeenIds((current) => {
      const next = new Set(current);
      ids.filter(Boolean).forEach((id) => next.add(id));
      return next.size === current.size ? current : next;
    });
  }, []);

  const isSeen = useCallback((id: string) => seenIds.has(id), [seenIds]);

  useEffect(() => persistIds(SEEN_STORAGE_KEY, seenIds), [seenIds]);

  return (
    <RiskFlowContext.Provider
      value={{
        alerts: visibleAlerts,
        ...counts,
        clearAll,
        removeAlert,
        markSeen,
        markAllSeen,
        isSeen,
        refresh: feed.refresh,
        refreshing: feed.refreshing,
        fetchStatus: feed.fetchStatus,
        loadMore: feed.loadMore,
        loadingMore: feed.loadingMore,
        hasMore: feed.hasMore,
        initialLoaded: feed.initialLoaded,
        freshAlertId,
      }}
    >
      {children}
    </RiskFlowContext.Provider>
  );
}

export function useRiskFlow(): RiskFlowContextValue {
  return useContext(RiskFlowContext);
}

function countBySeverity(alerts: RiskFlowAlert[]) {
  return {
    highCount: alerts.filter(
      (alert) => alert.severity === "high" || alert.severity === "critical",
    ).length,
    mediumCount: alerts.filter((alert) => alert.severity === "medium").length,
    lowCount: alerts.filter((alert) => alert.severity === "low").length,
  };
}

function loadStoredIds(key: string): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed)
      ? new Set(
          parsed.filter((value): value is string => typeof value === "string"),
        )
      : new Set();
  } catch {
    return new Set();
  }
}

function persistIds(key: string, ids: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {
    // Storage failure must not block the live feed.
  }
}
